import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import {
  DataTable, Drawer, ListLayout, Pagination, Panel, SearchInput, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass, labelClass
} from './AdminUI';

const PAGE_SIZE = 25;
const UNIT_TYPES = ['studio', '1 bedroom', '2 bedroom', '3 bedroom', '4 bedroom', 'townhouse'];
const EMPTY_UNIT = { number: '', parking: '', locker: '', bicycle: '', features: [], type: '', sqft: '' };

const list = (values) => (values || []).join(', ');
const parseList = (text) => text.split(',').map((s) => s.trim()).filter(Boolean);
const isSelfManaged = (condo) => (condo.selfManagement?.mode || 'off') !== 'off';
const unitIdOf = (user) => (user.unitRef && typeof user.unitRef === 'object' ? user.unitRef._id : user.unitRef);

function UnitForm({ condo, unit, onSaved, onCancel }) {
  const api = useAdminApi();
  const features = condo.features || [];
  const selfManaged = isSelfManaged(condo);
  const [form, setForm] = useState(() => (unit ? {
    entitlement: unit.entitlement?.toString() || '',
    ownersOfRecord: unit.ownersOfRecord || '',
    noticeAddress: unit.noticeAddress || '',
    number: unit.number?.toString() || '',
    parking: list(unit.parking),
    locker: list(unit.locker),
    bicycle: list(unit.bicycle),
    features: (unit.features || []).filter((f) => features.includes(f)),
    type: unit.type || '',
    sqft: unit.sqft?.toString() || ''
  } : { ...EMPTY_UNIT, entitlement: '', ownersOfRecord: '', noticeAddress: '' }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      number: form.number,
      parking: parseList(form.parking),
      locker: parseList(form.locker),
      bicycle: parseList(form.bicycle),
      features: form.features,
      type: form.type,
      sqft: form.sqft,
      ...(selfManaged ? { entitlement: form.entitlement === '' ? null : form.entitlement, ownersOfRecord: form.ownersOfRecord, noticeAddress: form.noticeAddress } : {})
    };
    try {
      if (unit) await api.put(`/api/admin/condos/${condo._id}/units/${unit._id}`, payload);
      else await api.post(`/api/admin/condos/${condo._id}/units`, payload);
      onSaved(unit ? 'Unit saved.' : 'Unit added.');
    } catch (err) {
      alert(errorMessage(err, 'Failed to save unit'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label htmlFor={`unit-${key}`} className={labelClass}>{label}</label>
      <input id={`unit-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputClass} {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {field('number', 'Unit number *', { type: 'number', min: '0', step: '1', required: true })}
        <div>
          <label htmlFor="unit-type" className={labelClass}>Type</label>
          <select id="unit-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputClass}>
            <option value="">Not specified</option>
            {UNIT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        {field('parking', 'Parking', { placeholder: 'e.g. P1, P2' })}
        {field('locker', 'Locker', { placeholder: 'e.g. L3' })}
        {field('bicycle', 'Bicycle storage', { placeholder: 'e.g. B2' })}
        {field('sqft', 'Sqft', { type: 'number', min: '0', step: '0.01' })}
        {selfManaged && field('entitlement', 'Unit entitlement', { type: 'number', min: '0', step: '0.0001' })}
      </div>
      {selfManaged && (
        <fieldset className="space-y-3 border-t border-border pt-3">
          <legend className="text-sm font-semibold text-foreground">Owner of record</legend>
          <p className="text-xs text-muted-foreground">From the land title and the strata's owner list; can differ from who signed up. Only admins and council see it.</p>
          <div>
            <label htmlFor="unit-ownersOfRecord" className={labelClass}>Owners</label>
            <textarea id="unit-ownersOfRecord" rows={2} value={form.ownersOfRecord} onChange={(e) => setForm({ ...form, ownersOfRecord: e.target.value })} className={inputClass} />
          </div>
          <div>
            <label htmlFor="unit-noticeAddress" className={labelClass}>Address for notices</label>
            <textarea id="unit-noticeAddress" rows={2} value={form.noticeAddress} onChange={(e) => setForm({ ...form, noticeAddress: e.target.value })} className={inputClass} placeholder="Where paper notices go if the owner hasn't agreed to email" />
          </div>
        </fieldset>
      )}
      {features.length > 0 && (
        <fieldset>
          <legend className={labelClass}>Features</legend>
          <div className="flex flex-wrap gap-3">
            {features.map((feature) => (
              <label key={feature} className="flex items-center gap-1 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.features.includes(feature)}
                  onChange={(e) => setForm({
                    ...form,
                    features: e.target.checked ? [...form.features, feature] : form.features.filter((f) => f !== feature)
                  })}
                />
                {feature}
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : unit ? 'Save unit' : 'Create Unit'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

function UnitImport({ condo, onApplied }) {
  const api = useAdminApi();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const parse = async () => {
    setParsing(true);
    setPreview(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await api.post(`/api/admin/condos/${condo._id}/units/bulk-parse`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to parse document'));
    } finally {
      setParsing(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/api/admin/condos/${condo._id}/units/bulk-apply`, { units: preview.units });
      const { created, updated, errors } = data.results;
      onApplied(`Applied: ${created} created, ${updated} updated.${errors.length ? ` Errors: ${errors.join('; ')}` : ''}`);
    } catch (err) {
      alert(errorMessage(err, 'Failed to apply unit data'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Panel className="mb-4 space-y-3">
      <p className="text-sm text-muted-foreground">Upload a CSV or TXT file describing your units. AI extracts unit numbers, parking, lockers, bicycle storage, type and sqft, and shows a preview before anything is saved.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept=".csv,.txt"
          aria-label="Unit file"
          onChange={(e) => setFile(e.target.files[0] || null)}
          className="text-sm text-foreground file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-primary file:text-primary-foreground"
        />
        <button type="button" onClick={parse} disabled={!file || parsing} className={btnPrimary}>{parsing ? 'Analyzing...' : 'Analyze file'}</button>
      </div>
      {preview && (
        <div className="space-y-3">
          {preview.summary && <p className="text-sm"><span className="font-medium">Summary:</span> {preview.summary}</p>}
          {preview.warnings?.length > 0 && (
            <ul className="text-sm text-warning space-y-1">{preview.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}</ul>
          )}
          {preview.units?.length > 0 ? (
            <>
              <DataTable
                label="Import preview"
                rowKey="number"
                rows={preview.units}
                columns={[
                  { key: 'number', header: 'Unit', className: 'font-medium' },
                  { key: 'parking', header: 'Parking', render: (u) => [].concat(u.parking ?? []).join(', ') || '—' },
                  { key: 'locker', header: 'Locker', render: (u) => [].concat(u.locker ?? []).join(', ') || '—' },
                  { key: 'bicycle', header: 'Bicycle', render: (u) => [].concat(u.bicycle ?? []).join(', ') || '—' },
                  { key: 'type', header: 'Type', className: 'capitalize', render: (u) => u.type || '—' },
                  { key: 'sqft', header: 'Sqft', render: (u) => u.sqft ?? '—' }
                ]}
              />
              <button type="button" onClick={apply} disabled={applying} className={btnPrimary}>{applying ? 'Applying...' : `Apply ${preview.units.length} unit(s)`}</button>
            </>
          ) : <p className="text-sm text-muted-foreground">No units could be extracted from this file.</p>}
        </div>
      )}
    </Panel>
  );
}

function UnitsTab({ condo, notify }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [units, setUnits] = useState(null);
  const [users, setUsers] = useState([]);
  const [importing, setImporting] = useState(false);

  const q = (params.get('q') || '').trim().toLowerCase();
  const page = Number(params.get('page')) || 1;
  const selectedId = params.get('unit');
  const creating = params.get('new') === '1';

  const load = useCallback(async () => {
    try {
      const [unitsResponse, usersResponse] = await Promise.all([
        api.get(`/api/admin/condos/${condo._id}/units`),
        api.get('/api/admin/users', { condo: condo._id })
      ]);
      setUnits(unitsResponse.data.units || []);
      setUsers(usersResponse.data.users || []);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch units'));
      setUnits([]);
    }
  }, [api, condo._id]);

  useEffect(() => { load(); }, [load]);

  const residentsByUnit = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const id = unitIdOf(u);
      if (!id) return;
      map.set(String(id), [...(map.get(String(id)) || []), u]);
    });
    return map;
  }, [users]);

  const filtered = useMemo(() => (units || [])
    .filter((u) => !q || [u.number, list(u.parking), list(u.locker), list(u.bicycle), u.type, list(u.features)]
      .some((v) => v != null && String(v).toLowerCase().includes(q)))
    .sort((a, b) => Number(a.number) - Number(b.number)), [units, q]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const selected = (units || []).find((u) => String(u._id) === String(selectedId));

  const saved = (message) => {
    notify(message);
    setParams({ new: null });
    load();
  };

  const remove = async (unit) => {
    if (!window.confirm(`Delete unit ${unit.number}? Linked residents keep their legacy unit number but lose this link.`)) return;
    try {
      await api.del(`/api/admin/condos/${condo._id}/units/${unit._id}`);
      notify('Unit deleted.');
      setParams({ unit: null });
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete unit'));
    }
  };

  const selfManaged = isSelfManaged(condo);
  const totalEntitlement = (units || []).reduce((sum, u) => sum + (Number(u.entitlement) || 0), 0);
  const columns = [
    { key: 'number', header: 'Unit', className: 'font-medium text-foreground', render: (u) => `Unit ${u.number}` },
    { key: 'type', header: 'Type', className: 'capitalize', render: (u) => u.type || '—' },
    { key: 'sqft', header: 'Sqft', hideBelow: 'md', render: (u) => (u.sqft != null ? `${u.sqft} sqft` : '—') },
    { key: 'parking', header: 'Parking', hideBelow: 'md', render: (u) => list(u.parking) || '—' },
    { key: 'locker', header: 'Locker', hideBelow: 'xl', render: (u) => list(u.locker) || '—' },
    { key: 'bicycle', header: 'Bicycle', hideBelow: 'xl', render: (u) => list(u.bicycle) || '—' },
    { key: 'features', header: 'Features', hideBelow: 'xl', render: (u) => list(u.features) || '—' },
    ...(selfManaged ? [{
      key: 'entitlement',
      header: 'Entitlement',
      hideBelow: 'lg',
      render: (u) => (u.entitlement != null ? `${u.entitlement}${totalEntitlement ? ` (${((u.entitlement / totalEntitlement) * 100).toFixed(2)}%)` : ''}` : '—')
    }] : []),
    { key: 'residents', header: 'Residents', render: (u) => (residentsByUnit.get(String(u._id)) || []).length }
  ];

  const drawer = creating ? (
    <Drawer title="New unit" onClose={() => setParams({ new: null })}>
      <UnitForm condo={condo} onSaved={saved} onCancel={() => setParams({ new: null })} />
    </Drawer>
  ) : selected ? (
    <Drawer title={`Unit ${selected.number}`} onClose={() => setParams({ unit: null })}>
      <UnitForm key={selected._id} condo={condo} unit={selected} onSaved={(message) => { notify(message); load(); }} onCancel={() => setParams({ unit: null })} />
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-foreground mb-2">Residents</h3>
        {(residentsByUnit.get(String(selected._id)) || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No residents linked to this unit.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {residentsByUnit.get(String(selected._id)).map((u) => (
              <li key={u._id} className="grid grid-cols-3 gap-2">
                <span className="font-medium text-foreground truncate">{u.name}</span>
                <span className="text-muted-foreground truncate">{u.email}</span>
                <span className="text-muted-foreground capitalize">{u.role}{!u.isActive && ' (pending)'}{selfManaged && u.noticeConsent && <span className="normal-case"> · notices by email</span>}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <div className="mt-6 pt-4 border-t border-border">
        <button type="button" onClick={() => remove(selected)} className={btnDanger}>Delete unit</button>
      </div>
    </Drawer>
  ) : null;

  if (!units) return <Spinner />;

  return (
    <ListLayout drawer={drawer}>
      {importing && <UnitImport condo={condo} onApplied={(message) => { notify(message); setImporting(false); load(); }} />}
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Units ({filtered.length}){selfManaged && totalEntitlement > 0 && <span className="text-sm font-normal text-muted-foreground"> · total entitlement {totalEntitlement}</span>}</h2>
          <SearchInput value={params.get('q') || ''} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Search unit, parking, locker, type..." />
          <button type="button" onClick={() => setImporting(!importing)} className={btnSecondary}>{importing ? 'Cancel import' : 'Import'}</button>
          <button type="button" onClick={() => setParams({ new: '1', unit: null })} className={btnPrimary}>+ Add Unit</button>
        </Toolbar>
        <DataTable
          label="Units"
          columns={columns}
          rows={rows}
          selectedId={selectedId}
          onRowClick={(u) => setParams({ unit: u._id, new: null })}
          emptyMessage={q ? 'No units match your search.' : 'No units yet.'}
        />
        <Pagination page={currentPage} pages={pages} total={filtered.length} limit={PAGE_SIZE} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
      </Panel>
    </ListLayout>
  );
}

export default UnitsTab;
